import type { Node } from "types/explain";

import { formatNumber, formatMs, formatBytes } from "utils/format";
import {
  getCumulativeTotalMetric,
  getSelfTotalMetric,
  getMaxParallelSelfTotalMetric,
} from "utils/explain";
import {
  ExplainAspect,
  ExplainSubAspect,
} from "features/Explain/WithExplainAspect";

const ExplainFormattedMetric = ({
  node,
  aspect,
  subAspect,
  blockSize,
}: {
  node: Node;
  aspect: ExplainAspect;
  subAspect: ExplainSubAspect | undefined;
  blockSize: number;
}) => {
  if (aspect === "Est. Cost") {
    const cost = getSelfTotalMetric(node, "Total Cost");

    return cost != null ? formatNumber(cost, 2) : "-";
  } else if (aspect === "Runtime") {
    const cost = getMaxParallelSelfTotalMetric(node, "Actual Total Time");

    return cost != null ? formatMs(cost) : "-";
  } else if (aspect == "Rows") {
    const metric = subAspect === "Actual" ? "Actual Rows" : "Plan Rows";
    const cost = getCumulativeTotalMetric(node, metric);
    return cost != null ? formatNumber(cost) : "-";
  } else if (aspect === "Buffers") {
    if (subAspect === "Hit + Read") {
      const metrics = [
        "Shared Hit Blocks",
        "Shared Read Blocks",
        "Local Hit Blocks",
        "Local Read Blocks",
      ] as const;

      const hasMetrics = metrics.some((metric) => metric in node);
      if (!hasMetrics) {
        return "-";
      }

      const cost = metrics.reduce((tot, metric) => {
        const partialCost = getSelfTotalMetric(node, metric);
        return tot + (partialCost ?? 0);
      }, 0);
      return formatNumber(cost);
    } else if (subAspect === "Dirtied") {
      const metrics = [
        "Shared Dirtied Blocks",
        "Local Dirtied Blocks",
      ] as const;
      const hasMetrics = metrics.some((metric) => metric in node);
      if (!hasMetrics) {
        return "-";
      }

      const cost = metrics.reduce((tot, metric) => {
        const partialCost = getSelfTotalMetric(node, metric);
        return tot + (partialCost ?? 0);
      }, 0);
      return formatNumber(cost);
    } else {
      return "-";
    }
  } else if (aspect === "Reads") {
    if (subAspect === "Time") {
      const legacyCost = getSelfTotalMetric(node, "I/O Read Time");
      const cost = getSelfTotalMetric(node, "Shared I/O Read Time");
      return cost != null
        ? formatMs(cost)
        : legacyCost != null
        ? formatMs(legacyCost)
        : "-";
    } else if (subAspect === "Bytes") {
      const metrics = ["Shared Read Blocks", "Local Read Blocks"] as const;
      const hasMetrics = metrics.some((metric) => metric in node);
      if (!hasMetrics) {
        return "-";
      }

      const cost = metrics.reduce((tot, metric) => {
        const partialCost = getSelfTotalMetric(node, metric);
        return tot + (partialCost ?? 0);
      }, 0);
      return formatBytes(cost * blockSize);
    } else {
      return "-";
    }
  } else if (aspect === "Writes") {
    if (subAspect === "Time") {
      const legacyCost = getSelfTotalMetric(node, "I/O Write Time");
      const cost = getSelfTotalMetric(node, "Shared I/O Write Time");
      return cost != null
        ? formatMs(cost)
        : legacyCost != null
        ? formatMs(legacyCost)
        : "-";
    } else if (subAspect === "Bytes") {
      const metrics = [
        "Shared Written Blocks",
        "Local Written Blocks",
      ] as const;
      const hasMetrics = metrics.some((metric) => metric in node);
      if (!hasMetrics) {
        return "-";
      }

      const cost = metrics.reduce((tot, metric) => {
        const partialCost = getSelfTotalMetric(node, metric);
        return tot + (partialCost ?? 0);
      }, 0);
      return formatBytes(cost * blockSize);
    } else {
      return "-";
    }
  } else {
    return "-";
  }
};

export default ExplainFormattedMetric;
